import { useCallback, useEffect, useState } from "react";

import type { AlarmRow } from "./AlarmBanner.js";
import type { Cluster } from "./ClusterCard.js";
import { Toggle } from "./Toggle.js";
import { formatWhen, sortAlarms, stateLabel } from "../alarm-view.js";
import { ApiError } from "../api/client.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * La scheda GESTIONE ALLARMI, portata da `SettingsAlarmsTab.qml`.
 *
 * Non e' una pagina di interruttori: e' il **cruscotto** degli allarmi. In
 * cima una carta che dice in una riga come sta il sistema, sotto le metriche
 * che servono a capire se qualcosa non funziona nel modo in cui gli allarmi
 * vengono gestiti — non nell'impianto.
 *
 * Le due che contano piu' delle altre:
 *
 *  - la **raffica**: dieci allarmi in dieci minuti non e' dieci volte un
 *    allarme, e' il momento in cui l'operatore smette di guardarli.
 *  - i **vecchi non confermati**: un allarme aperto da un giorno non e'
 *    urgente, e' dimenticato. Sono i due modi in cui una sorveglianza smette
 *    di sorvegliare senza che niente si rompa.
 */

interface Kpis {
  totalAlarms?: number;
  activeAlarms?: number;
  shelvedAlarms?: number;
  staleAlarms?: number;
  unacknowledged?: number;
  alarmRatePerHour?: number;
  maxAlarmsIn10Min?: number;
  floodDetected?: boolean;
  priorityDistribution?: Record<string, number>;
  badActors?: { clusterId: string; count: number }[];
  avgAckTimeSeconds?: number;
}

interface AuditRow {
  id: number;
  alarmId: string;
  clusterId: string;
  action: string;
  details: string | null;
  performedBy: string | null;
  createdAt: string;
}

export function AlarmsTab() {
  const { t } = useI18n();
  const { api, can } = useSession();

  const [enabled, setEnabled] = useState<boolean | undefined>();
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [kpis, setKpis] = useState<Kpis>({});
  const [alarms, setAlarms] = useState<AlarmRow[]>([]);
  const [shelved, setShelved] = useState<AlarmRow[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  /** La sigla d'area sotto il nome nella classifica: due impianti che si
   *  chiamano quasi uguale si distinguono da quella, non dal nome. */
  const [areas, setAreas] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);

  const fail = useCallback((e: unknown) => {
    if (e instanceof ApiError && e.isForbidden) setDenied(true);
    else setError(e instanceof ApiError ? e.message : String(e));
  }, []);

  const reload = useCallback(async () => {
    try {
      const [en, st, k, a, sh, au, cl] = await Promise.all([
        api.call<{ enabled: boolean }>("alarms.enabled", {}),
        api.call<Record<string, string>>("settings.list"),
        api.call<Kpis>("alarms.kpis"),
        api.call<AlarmRow[]>("alarms.list"),
        api.call<AlarmRow[]>("alarms.shelved"),
        api.call<AuditRow[]>("audit-log.list", { limit: 50 }),
        api.call<Cluster[]>("clusters.list"),
      ]);
      setEnabled(en.enabled);
      setSettings(st);
      setKpis(k);
      setAlarms(a);
      setShelved(sh);
      setAudit(au);
      setNames(Object.fromEntries(cl.map((c) => [c.id, c.name])));
      setAreas(Object.fromEntries(cl.map((c) => [c.id, c.areaCode ?? ""])));
      setError("");
      setDenied(false);
    } catch (e) {
      fail(e);
    }
  }, [api, fail]);

  useEffect(() => void reload(), [reload]);

  const act = async (route: Parameters<typeof api.call>[0], body?: unknown) => {
    try {
      await api.call(route, body);
      await reload();
    } catch (e) {
      fail(e);
    }
  };

  if (denied) {
    return (
      <div className="settings__pane">
        <p className="muted">{t("users.superAdminRequired")}</p>
      </div>
    );
  }

  const unacked = kpis.unacknowledged ?? 0;
  const stale = kpis.staleAlarms ?? 0;
  const flood = kpis.floodDetected === true;
  const state = !enabled ? "disabled" : flood ? "critical" : unacked > 0 ? "warning" : stale > 0 ? "stale" : "normal";
  const title =
    state === "disabled"
      ? t("alarm.hero.disabled")
      : state === "critical"
        ? t("alarm.hero.flood")
        : state === "warning"
          ? t("alarm.hero.unacked", { n: unacked })
          : state === "stale"
            ? t("alarm.hero.stale", { n: stale })
            : t("alarm.hero.allClear");
  const subtitle = [
    kpis.activeAlarms !== undefined ? `${kpis.activeAlarms} active` : "",
    kpis.totalAlarms !== undefined ? `${kpis.totalAlarms} total` : "",
    kpis.alarmRatePerHour !== undefined ? `${kpis.alarmRatePerHour}/h` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const prio = kpis.priorityDistribution ?? {};

  return (
    <div className="settings__pane analytics">
      {error && <p className="alert">{error}</p>}

      <div className={`ahero ahero--${state}`}>
        <span className="ahero__dot">
          <i />
        </span>
        <div className="ahero__text">
          <h3>{title}</h3>
          <p className="muted small">{subtitle || t("alarm.hero.warmingUp")}</p>
        </div>
        {/* I tre interruttori stanno QUI, insieme, come nel .qml: armare
            gli allarmi, l'avviso acustico e il fermo della ronda su L2 sono
            la stessa decisione vista da tre lati, e spargerli per la pagina
            faceva sembrare che non c'entrassero l'uno con l'altro. */}
        <div className="ahero__switches">
          <HeroSwitch
            label={t("alarm.toggle.label")}
            on={enabled === true}
            disabled={!can("manageAlarms")}
            onChange={(v) => void act("alarms.enabled", { enabled: v })}
          />
          <HeroSwitch
            label={t("alarm.audioAlert.label")}
            on={settings["audio_alert_enabled"] === "true"}
            disabled={!can("manageAlarms")}
            onChange={(v) =>
              void act("settings.update", { audio_alert_enabled: v ? "true" : "false" })
            }
          />
          <HeroSwitch
            label={t("alarm.stopPatrol.label")}
            on={settings["stop_patrol_on_critical"] === "true"}
            disabled={!can("manageAlarms")}
            onChange={(v) =>
              void act("settings.update", { stop_patrol_on_critical: v ? "true" : "false" })
            }
          />
        </div>
      </div>

      <h3 className="admin__title">{t("hdr.performanceMetrics")}</h3>
      <div className="ametrics ametrics--4">
        <Metric label={t("alarm.kpi.alarmRate")} value={`${kpis.alarmRatePerHour ?? 0}/h`} />
        <Metric
          label={t("alarm.kpi.unacknowledged")}
          value={String(unacked)}
          tone={unacked > 0 ? "crit" : undefined}
        />
        <Metric
          label={t("alarm.kpi.staleAlarms")}
          value={String(stale)}
          tone={stale > 0 ? "warn" : undefined}
        />
        <Metric
          label={t("alarm.kpi.avgAckTime")}
          value={formatDuration(kpis.avgAckTimeSeconds ?? 0)}
        />
      </div>

      <h3 className="admin__title">{t("hdr.priorityDistribution")}</h3>
      <div className="ametrics">
        {(["HIGH", "MEDIUM", "LOW"] as const).map((p) => (
          <div key={p}>
            <div className="ametric__label">{t(`alarm.priority.${p}`)}</div>
            <div className={`ametric__value ametric__value--${p === "HIGH" ? "crit" : p === "MEDIUM" ? "warn" : ""}`}>
              {prio[p] ?? 0}
            </div>
          </div>
        ))}
      </div>

      <h3 className="admin__title">{t("alarm.kpi.topBadActors")}</h3>
      {/* La barra e' il motivo per cui questo riquadro esiste: dice a colpo
          d'occhio se un impianto fa il doppio degli allarmi degli altri o se
          sono tutti uguali. Due colonne di numeri quella lettura non la danno.
          E' proporzionale al primo della lista, come nel .qml. */}
      <ol className="badactors">
        {(kpis.badActors ?? []).map((b, i) => {
          const worst = kpis.badActors?.[0]?.count || 1;
          return (
            <li key={b.clusterId}>
              <span className="badactors__rank">#{i + 1}</span>
              <span className="badactors__id">
                <b>{names[b.clusterId] ?? b.clusterId}</b>
                {areas[b.clusterId] && <em>{areas[b.clusterId]}</em>}
              </span>
              <span className="badactors__bar">
                <i style={{ width: `${Math.min(100, (b.count / worst) * 100)}%` }} />
              </span>
              <span className="badactors__count">{b.count}</span>
            </li>
          );
        })}
        {(kpis.badActors ?? []).length === 0 && <li className="muted">{t("home.noAlarms")}</li>}
      </ol>

      {/* I silenziati restano visibili: uno sparito dalla lista e' uno che
          nessuno rivede, e la sua scadenza arriva senza che nessuno la
          aspetti. */}
      {shelved.length > 0 && (
        <>
          <h3 className="admin__title">{t("alarm.state.SHLVD")}</h3>
          <div className="ulist">
            {shelved.map((a) => (
              <div className="ccard" key={a.id}>
                <div className="ccard__row">
                  <b>{names[a.clusterId] ?? a.clusterId}</b>
                  <span className="muted small">
                    {t("alarm.shelvedBy")}: {(a as { shelvedBy?: string }).shelvedBy ?? "—"}
                  </span>
                  <span className="emissions__spacer" />
                  {can("acknowledgeAlarms") && (
                    <button onClick={() => void act("alarms.unshelve", { id: a.id })}>
                      {t("alarm.unshelve")}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <h3 className="admin__title">{t("hdr.eventLog")}</h3>
      <div className="atable">
        <table className="table table--zebra">
          <thead>
            <tr>
              <th>{t("sitemap.cluster")}</th>
              <th>{t("alarm.audit.action")}</th>
              <th className="ta-right">PPM·M</th>
              <th>{t("focus.eventDate")}</th>
            </tr>
          </thead>
          <tbody>
            {sortAlarms(alarms)
              .slice(0, 30)
              .map((a) => (
                <tr key={a.id}>
                  <td>{names[a.clusterId] ?? a.clusterId}</td>
                  <td>
                    <span className={a.level === "L2" ? "ko" : "warn"}>{a.level}</span>{" "}
                    {stateLabel(a.state, t)}
                  </td>
                  <td className="ta-right mono">
                    {a.ppmValue === null ? "—" : Number(a.ppmValue).toFixed(1)}
                  </td>
                  <td className="muted">{formatWhen(a.createdAt)}</td>
                </tr>
              ))}
            {alarms.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  {t("home.noAlarms")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Chi ha confermato cosa e quando: e' la parte che serve dopo, quando
          si ricostruisce una notte. */}
      <h3 className="admin__title">{t("alarm.audit.title")}</h3>
      <div className="atable">
        <table className="table table--zebra">
          <thead>
            <tr>
              <th>{t("alarm.audit.action")}</th>
              <th>{t("alarm.audit.details")}</th>
              <th>{t("alarm.audit.performedBy")}</th>
              <th>{t("focus.eventDate")}</th>
            </tr>
          </thead>
          <tbody>
            {audit.map((r) => (
              <tr key={r.id}>
                <td className={auditTone(r.action)}>{r.action}</td>
                <td className="muted">{r.details ?? "—"}</td>
                <td>{r.performedBy ?? "—"}</td>
                <td className="muted">{formatWhen(r.createdAt)}</td>
              </tr>
            ))}
            {audit.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  {t("home.noAlarms")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: "crit" | "warn" | undefined }) {
  return (
    <div>
      <div className="ametric__label">{label.toUpperCase()}</div>
      <div className={`ametric__value${tone ? ` ametric__value--${tone}` : ""}`}>{value}</div>
    </div>
  );
}

/** Secondi in una forma leggibile: "—" quando non c'e' ancora nessuna
 *  conferma da cui ricavare una media, che non e' zero secondi. */
function formatDuration(seconds: number): string {
  if (!seconds) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${seconds % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function auditTone(action: string): string {
  // L'AZIONE dell'audit, non lo stato: qui "ACK" e' il nome del gesto.
  if (action === "ACK") return "ok";
  if (action === "SHELVE") return "warn";
  if (action === "OUT_OF_SERVICE") return "ko";
  return "";
}

function HeroSwitch({
  label,
  on,
  disabled,
  onChange,
}: {
  label: string;
  on: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="heroswitch">
      <span className="heroswitch__label">{label.toUpperCase()}</span>
      <div className="heroswitch__row">
        {/* La parola sta ACCANTO alla levetta, non dentro: dentro un bottone
            "ON" si legge come il comando da premere per accendere, e a chi
            arma gli allarmi quella ambiguita' non si puo' dare. */}
        <span className={`heroswitch__state${on ? " heroswitch__state--on" : ""}`}>
          {on ? t("alarm.toggle.on") : t("alarm.toggle.off")}
        </span>
        <Toggle on={on} disabled={disabled} onChange={onChange} title={label} />
      </div>
    </div>
  );
}
